import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useCookieConsent } from "../../context/CookieConsentContext";
import { useTranslation } from "react-i18next";
import Typography from "../Typography";
import { theme } from "../../theme";
import Button from "../Button/Button";
import { Link } from "react-router-dom";
import CookieMonsterIcon from "../Icons/CookieMonsterIcon";
import Switch from "../Switch";
import { isTrackingUiEnabled } from "../../config/analyticsFeatureFlags";

export default function CookieBanner() {
	const {
		isBannerVisible,
		panelOpen,
		consent,
		updateConsent,
		openConfigPanel,
		closeConfigPanel,
		withdrawalStatus,
		dismissWithdrawal,
		retryWithdrawal,
	} = useCookieConsent();
	const [draftInternal, setDraftInternal] = useState(
		consent?.analyticsInternal === true,
	);
	const [draftClarity, setDraftClarity] = useState(
		consent?.analyticsClarity === true,
	);
	const { t } = useTranslation();

	useEffect(() => {
		if (panelOpen) {
			setDraftInternal(consent?.analyticsInternal === true);
			setDraftClarity(consent?.analyticsClarity === true);
		}
	}, [panelOpen, consent]);

	const showWithdraw = isTrackingUiEnabled && consent?.analyticsInternal === true && !panelOpen

	if (!isBannerVisible && !panelOpen && !showWithdraw) return null;

	return (
		<CookieRow>
			{!consent && !panelOpen && (
				<Banner>
					<Container>
						<CookieMsg>
							<CookieMonsterIcon
								height={20}
								width={20}
								style={{ minWidth: 20 }}
							/>
							<CookieMsgText>
								<Typography
									as="p"
									$variant="body-2-medium"
									color={theme.fill.defaultAlphaMain80}
								>
									{t("cookies:banner.message")}
								</Typography>
								<Typography
									as="p"
									$variant="body-2-medium"
									color={theme.fill.defaultAlphaMain80}
								>
									<CookieLink onClick={openConfigPanel}>
										{t("cookies:banner.configure")}
									</CookieLink>{" "}
									{t("cookies:banner.learnMorePrefix")}{" "}
									<CookieLink to="/privacy-policy" target="_blank">
										{t("cookies:banner.learnMoreLink")}
									</CookieLink>
									.
								</Typography>
							</CookieMsgText>
						</CookieMsg>
						<Actions>
							<CookieActionBtn
								onClick={() => updateConsent({ analyticsInternal: true, analyticsClarity: true })}
								$variant="defaultInverse"
								size="m"
								sizeTablet="m"
								sizeMobile="m"
							>
								{t("cookies:banner.acceptAll")}
							</CookieActionBtn>
							<CookieActionBtn
								onClick={() => updateConsent({ analyticsInternal: false, analyticsClarity: false })}
								$variant="outlineInverse"
								size="m"
								sizeTablet="m"
								sizeMobile="m"
							>
								{t("cookies:banner.reject")}
							</CookieActionBtn>
						</Actions>
					</Container>
				</Banner>
			)}
			{showWithdraw && (
			<WithdrawBar>
				<WithdrawBtn
					onClick={openConfigPanel}
					aria-label={t("cookies:withdraw.ariaLabel")}
				>
					{t("cookies:withdraw.button")}
				</WithdrawBtn>
			</WithdrawBar>
		)}
		{panelOpen && (
				<Panel>
					<PanelTitle
						as="h4"
						$variant="body-1-semibold"
						color={theme.colors.defaultMain}
					>
						{t("cookies:panel.title")}
					</PanelTitle>
					{withdrawalStatus !== 'idle' ? (
						<>
							<Row>
								<Typography
									as="p"
									$variant="body-2-medium"
									color={theme.colors.defaultMain}
								>
									{withdrawalStatus === 'pending' && `⏳ ${t("cookies:panel.withdrawal.pending")}`}
									{withdrawalStatus === 'success' && t("cookies:panel.withdrawal.success")}
									{withdrawalStatus === 'error' && t("cookies:panel.withdrawal.error")}
								</Typography>
							</Row>
							<PanelActions>
								{withdrawalStatus === 'error' && (
									<Button
										onClick={retryWithdrawal}
										$variant=""
										size="m"
										sizeTablet="m"
										sizeMobile="m"
									>
										{t("cookies:panel.withdrawal.retry")}
									</Button>
								)}
								{withdrawalStatus !== 'pending' && (
									<Button
										onClick={dismissWithdrawal}
										$variant="outline"
										size="m"
										sizeTablet="m"
										sizeMobile="m"
									>
										{t("cookies:panel.withdrawal.close")}
									</Button>
								)}
							</PanelActions>
						</>
					) : (
						<>
							<Row>
								<RowInfo>
									<Typography
										as="span"
										$variant="body-2-semibold"
										color={theme.colors.defaultStrong}
									>
										{t("cookies:panel.sections.necessary.title")}
									</Typography>
									<Typography
										as="span"
										$variant="caption-medium"
										color={theme.colors.defaultWeak}
									>
										{t("cookies:panel.sections.necessary.description")}
									</Typography>
								</RowInfo>
								<Badge $variant="caption-medium" color={theme.colors.defaultWeak}>
									{t("cookies:panel.sections.necessary.badge")}
								</Badge>
							</Row>
							<Row>
								<RowInfo>
									<Typography
										as="span"
										$variant="body-2-semibold"
										color={theme.colors.defaultStrong}
									>
										{t("cookies:panel.sections.analyticsInternal.title")}
									</Typography>
									<Typography
										as="span"
										$variant="caption-medium"
										color={theme.colors.defaultWeak}
									>
										{t("cookies:panel.sections.analyticsInternal.description")}
									</Typography>
								</RowInfo>
								<Switch
									value={draftInternal}
									ariaLabel={t("cookies:panel.sections.analyticsInternal.title")}
									onChange={(next) => setDraftInternal(next)}
								/>
							</Row>
							<Row>
								<RowInfo>
									<Typography
										as="span"
										$variant="body-2-semibold"
										color={theme.colors.defaultStrong}
									>
										{t("cookies:panel.sections.analyticsClarity.title")}
									</Typography>
									<Typography
										as="span"
										$variant="caption-medium"
										color={theme.colors.defaultWeak}
									>
										{t("cookies:panel.sections.analyticsClarity.description")}
									</Typography>
								</RowInfo>
								<Switch
									value={draftClarity}
									ariaLabel={t("cookies:panel.sections.analyticsClarity.title")}
									onChange={(next) => setDraftClarity(next)}
								/>
							</Row>
							<PanelActions>
								<Button
									onClick={() => updateConsent({ analyticsInternal: draftInternal, analyticsClarity: draftClarity })}
									$variant=""
									size="m"
									sizeTablet="m"
									sizeMobile="m"
									disabled={withdrawalStatus === 'pending'}
								>
									{t("cookies:panel.actions.save")}
								</Button>
								<Button
									onClick={closeConfigPanel}
									$variant="outline"
									size="m"
									sizeTablet="m"
									sizeMobile="m"
									disabled={withdrawalStatus === 'pending'}
								>
									{t("cookies:panel.actions.cancel")}
								</Button>
							</PanelActions>
						</>
					)}
				</Panel>
			)}
		</CookieRow>
	);
}

const CookieRow = styled.div`
  width: 100%;
  position: fixed;
  left: 0px;
  bottom: 0px;
  right: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  z-index: 1000;
  width: 100%;
  max-width: 1400px;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction:  column;
    justify-content: start;
  }
`;

const Banner = styled.div`
  background: ${({ theme }) => theme.fill.inverseMain};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const CookieMsg = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  align-items: center;
`;

const CookieMsgText = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
`;

const CookieLink = styled(Link)`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.inverseMain};
  transition: all 0.3s ease-out;

  &:hover {
    opacity: 0.5;
  }


`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  align-items: center;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const CookieActionBtn = styled(Button)`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-grow: 1;
  }
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.fill.defaultMain};
  color: ${({ theme }) => theme.colors.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  max-width: 600px;
`;

const PanelTitle = styled(Typography)`
  padding-bottom: ${({ theme }) => theme.sizing.xxs};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.xs} 0px;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const RowInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs}
`;

// const RowTitle = styled.span`
//   font-size: 14px;
//   color: ${({ theme }) => theme.colors.defaultStrong};
// `

// const RowDesc = styled.span`
//   font-size: 12px;
//   color: ${({ theme }) => theme.colors.defaultSubtle};
// `

// const Badge = styled.span`
//   font-size: 12px;
//   border: 1px solid ${({ theme }) => theme.border.defaultWeak};
//   border-radius: ${({ theme }) => theme.radius.xs};
//   padding: 6px 10px;
//   color: ${({ theme }) => theme.colors.defaultWeak};
//   background: ${({ theme }) => theme.fill.defaultSubtle};
// `

const Badge = styled(Typography)`
  padding: ${({ theme }) => theme.sizing.xxs} ${({ theme }) => theme.sizing.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: 24px;
`;

const PanelActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: flex-start;
  padding-top: ${({ theme }) => theme.sizing.sm};
`;

const WithdrawBar = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.xxs} ${({ theme }) => theme.sizing.sm};
  background: ${({ theme }) => theme.fill.defaultMain};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const WithdrawBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.defaultWeak};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.defaultMain};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryMain};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
